import { isPlatformRole, type PlatformRole } from './roles';
import type { AccessProvider, AccessState, Principal } from './types';

/**
 * A provider with nothing to authenticate: `resolvePrincipal`/
 * `getSessionToken` both resolve to the "no active session" values the
 * {@link AccessProvider} contract already defines for that case (see
 * `types.ts`) — this is not an error path, it is the designed shape for "no
 * session", the same shape the federated provider will produce once it
 * exists and finds no corporate session either. `endSession` is a no-op:
 * there is nothing to tear down when nothing was ever established.
 */
const noSessionProvider: AccessProvider = {
  async resolvePrincipal() {
    return null;
  },
  async resolveAccessState() {
    return { kind: 'anonymous' };
  },
  async getSessionToken() {
    return null;
  },
  async endSession() {
    // Nothing to tear down: no session was ever established.
  },
};

/**
 * Local development implementation of {@link AccessProvider}. Maps a
 * development bearer token to a fixed principal and role set read from
 * build-time configuration (never committed — see `.env.example`). This is
 * not an authentication flow: there is no login form, no credential entry
 * screen, and nothing is persisted to storage. It exists solely so the
 * application shell and route guards are exercisable before the real
 * provider is wired in a later phase.
 *
 * `VITE_ACCESS_LOCAL_TOKEN` is a bearer token, so it can never be one of
 * the variables `deploy/package/build-artifact.sh` allows into a built
 * bundle (see that script's `SECRET_NAME_PATTERN`/`ALLOWED_BUILD_VARS`) —
 * inlining it into public JS would publish a real credential. A deployed
 * build is therefore always "unconfigured" here by design, not by mistake.
 * Rather than throw and crash the whole application at startup (a thrown
 * error at this module's eval time takes the entire bundle down before
 * React ever mounts), an unconfigured local provider resolves to
 * {@link noSessionProvider}: the shell, branding and routing still render,
 * every screen renders its own already-designed unauthenticated state (see
 * `mapErrorCode('unauthenticated')` in `src/api/errors.ts`), and no API
 * call ever carries a token. This is honest — it is exactly how the
 * federated provider will behave when the corporate session is absent —
 * and it keeps the token out of the bundle, which is the point of the gate
 * it would otherwise be tempting to route around.
 */
export function createLocalAccessProvider(): AccessProvider {
  const token = import.meta.env.VITE_ACCESS_LOCAL_TOKEN;
  const principalId = import.meta.env.VITE_ACCESS_LOCAL_PRINCIPAL_ID;
  const principalName = import.meta.env.VITE_ACCESS_LOCAL_PRINCIPAL_NAME;
  const rolesRaw = import.meta.env.VITE_ACCESS_LOCAL_ROLES;

  if (!token || !principalId || !principalName || !rolesRaw) {
    return noSessionProvider;
  }

  const roles = rolesRaw
    .split(',')
    .map((role) => role.trim())
    .filter((role): role is PlatformRole => isPlatformRole(role));

  if (roles.length === 0) {
    return noSessionProvider;
  }

  const principal: Principal = { id: principalId, displayName: principalName, roles };
  const authorizedState: AccessState = { kind: 'authorized', principal };

  return {
    async resolvePrincipal() {
      return principal;
    },
    async resolveAccessState() {
      return authorizedState;
    },
    async getSessionToken() {
      return token;
    },
    async endSession() {
      // The local provider has nothing to tear down: the principal comes
      // from build-time configuration, not a stored session.
    },
  };
}
